import React, { useState, useEffect } from 'react';
import './PromotionModal.css';

const PromotionModal = ({ isOpen, onClose, onSave, promotion = null, availableTemplates = [] }) => {
  const [formData, setFormData] = useState({
    title: '',
    type: 'Discount',
    status: 'Draft',
    discountType: 'percentage',
    discountValue: '',
    code: '',
    description: '',
    image: '🎯',
    startDate: '',
    endDate: '',
    targetAudience: 'All Customers',
    templateId: '',
    templateName: '',
    templateStatus: ''
  });
  const [errors, setErrors] = useState({});
  const [templateVerified, setTemplateVerified] = useState(false);
  const [templateError, setTemplateError] = useState('');

  const isEditMode = !!promotion;

  const types = [
    'Discount',
    'Flash Sale',
    'New Product',
    'Event'
  ];

  const discountTypes = [
    { value: 'percentage', label: 'Percentage Off' },
    { value: 'fixed', label: 'Fixed Amount Off' },
    { value: 'bogo', label: 'Buy One Get One' },
    { value: 'shipping', label: 'Free Shipping' }
  ];

  const targetAudiences = [
    'All Customers',
    'Active Customers',
    'New Customers',
    'VIP Customers',
    'Inactive Customers'
  ];

  const emojis = ['🎯', '🎉', '💰', '🛍️', '🎁', '⚡', '🔥', '💝', '🌞', '❄️', '🎄', '🎃', '🎊', '🏆', '⭐'];

  useEffect(() => {
    if (promotion) {
      setFormData({
        title: promotion.title || '',
        type: promotion.type || 'Discount',
        status: promotion.status || 'Draft',
        discountType: promotion.discountType || 'percentage',
        discountValue: promotion.discountValue || '',
        code: promotion.code || '',
        description: promotion.description || '',
        image: promotion.image || '🎯',
        startDate: promotion.startDate || '',
        endDate: promotion.endDate || '',
        targetAudience: promotion.targetAudience || 'All Customers',
        templateId: promotion.templateId || '',
        templateName: promotion.templateName || '',
        templateStatus: promotion.templateStatus || ''
      });
      setTemplateVerified(promotion.templateId && promotion.templateStatus === 'APPROVED');
    } else {
      const today = new Date().toISOString().split('T')[0];
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      setFormData({
        title: '',
        type: 'Discount',
        status: 'Draft',
        discountType: 'percentage',
        discountValue: '',
        code: '',
        description: '',
        image: '🎯',
        startDate: today,
        endDate: nextWeek.toISOString().split('T')[0],
        targetAudience: 'All Customers',
        templateId: '',
        templateName: '',
        templateStatus: ''
      });
      setTemplateVerified(false);
    }
    setErrors({});
    setTemplateError('');
  }, [promotion, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectTemplate = (template) => {
    // Verify template status before linking
    if (template.status !== 'APPROVED') {
      setTemplateError(`Template must be APPROVED to use in promotions. Current status: ${template.status}`);
      setTemplateVerified(false);
      return;
    }

    setFormData(prev => ({
      ...prev,
      templateId: template.id,
      templateName: template.name || template.displayName,
      templateStatus: template.status
    }));
    setTemplateVerified(true);
    setTemplateError('');
  };

  const handleRemoveTemplate = () => {
    setFormData(prev => ({
      ...prev,
      templateId: '',
      templateName: '',
      templateStatus: ''
    }));
    setTemplateVerified(false);
    setTemplateError('');
  };

  const generateCode = () => {
    const prefix = formData.title.substring(0, 4).toUpperCase().replace(/\s/g, '');
    const random = Math.floor(Math.random() * 1000);
    return `${prefix}${random}`;
  };

  const handleGenerateCode = () => {
    if (formData.title) {
      setFormData(prev => ({
        ...prev,
        code: generateCode()
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Promotion title is required';
    }

    if (!formData.templateId) {
      newErrors.templateId = 'Template selection is required';
    }

    if (!templateVerified) {
      newErrors.templateId = 'Selected template must be APPROVED';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (formData.discountType !== 'bogo' && formData.discountType !== 'shipping') {
      if (!formData.discountValue || parseFloat(formData.discountValue) <= 0) {
        newErrors.discountValue = 'Discount value is required';
      }
      if (formData.discountType === 'percentage' && parseFloat(formData.discountValue) > 100) {
        newErrors.discountValue = 'Percentage cannot exceed 100%';
      }
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Promo code is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate) {
      if (new Date(formData.endDate) < new Date(formData.startDate)) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const promotionData = {
        ...formData,
        discountValue: formData.discountType === 'bogo' || formData.discountType === 'shipping' 
          ? 100 
          : parseFloat(formData.discountValue)
      };
      onSave(promotionData);
      onClose();
    }
  };

  if (!isOpen) return null;

  const getMinDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content promotion-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Promotion' : 'Create New Promotion'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="section-divider">
            <h3>Template Selection (Required) *</h3>
            <p className="section-description">Select an APPROVED WhatsApp template to use for this promotion</p>
          </div>

          <div className="form-group">
            <label htmlFor="templateId">WhatsApp Template *</label>
            {formData.templateId ? (
              <div className="template-selected-box">
                <div className="template-selected-content">
                  <div className="template-selected-info">
                    <div className="template-selected-name">✓ {formData.templateName}</div>
                    <div className="template-selected-status">
                      <span className="status-badge status-approved">APPROVED</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-remove-template"
                    onClick={handleRemoveTemplate}
                    title="Remove template"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div className="template-selection-list">
                {availableTemplates && availableTemplates.length > 0 ? (
                  availableTemplates.filter(t => t.status === 'APPROVED').map(template => (
                    <div
                      key={template.id}
                      className={`template-option ${formData.templateId === template.id ? 'selected' : ''}`}
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <div className="template-option-header">
                        <div className="template-option-name">{template.name || template.displayName}</div>
                        <span className="status-badge status-approved">{template.status}</span>
                      </div>
                      <div className="template-option-body">
                        {template.components?.body?.text?.substring(0, 100)}...
                      </div>
                      {template.variables && template.variables.length > 0 && (
                        <div className="template-variables">
                          Variables: {template.variables.join(', ')}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-templates-message">
                    <p>No APPROVED templates available.</p>
                    <p className="small">Create and approve a MARKETING template first.</p>
                  </div>
                )}
              </div>
            )}
            {errors.templateId && <span className="error-message">{errors.templateId}</span>}
            {templateError && <span className="error-message">{templateError}</span>}
          </div>

          <div className="section-divider">
            <h3>Promotion Details</h3>
          </div>

          <div className="form-group">
            <label htmlFor="title">Promotion Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Summer Sale - 50% Off"
              className={errors.title ? 'error' : ''}
            />
            {errors.title && <span className="error-message">{errors.title}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Promotion Type</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="form-select"
              >
                {types.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-select"
              >
                <option value="Draft">Draft</option>
                <option value="Scheduled">Scheduled</option>
                <option value="Active">Active</option>
                <option value="Paused">Paused</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your promotion. This will be sent to customers..."
              rows="4"
              className={errors.description ? 'error' : ''}
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          <div className="section-divider">
            <h3>Discount Configuration</h3>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="discountType">Discount Type</label>
              <select
                id="discountType"
                name="discountType"
                value={formData.discountType}
                onChange={handleChange}
                className="form-select"
              >
                {discountTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            {(formData.discountType === 'percentage' || formData.discountType === 'fixed') && (
              <div className="form-group">
                <label htmlFor="discountValue">
                  {formData.discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount (USD)'} *
                </label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    id="discountValue"
                    name="discountValue"
                    value={formData.discountValue}
                    onChange={handleChange}
                    placeholder={formData.discountType === 'percentage' ? '50' : '25.00'}
                    step={formData.discountType === 'percentage' ? '1' : '0.01'}
                    min="0"
                    max={formData.discountType === 'percentage' ? '100' : undefined}
                    className={errors.discountValue ? 'error' : ''}
                  />
                  <span className="input-suffix">
                    {formData.discountType === 'percentage' ? '%' : '$'}
                  </span>
                </div>
                {errors.discountValue && <span className="error-message">{errors.discountValue}</span>}
              </div>
            )}
            {(formData.discountType === 'bogo' || formData.discountType === 'shipping') && (
              <div className="form-group">
                <label>Special Offer</label>
                <div className="special-offer-display">
                  {formData.discountType === 'bogo' ? 'Buy One Get One Free' : 'Free Shipping'}
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="code">Promo Code *</label>
            <div className="input-with-action">
              <input
                type="text"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="SUMMER50"
                className={errors.code ? 'error' : ''}
                style={{ textTransform: 'uppercase' }}
              />
              <button
                type="button"
                className="btn-generate"
                onClick={handleGenerateCode}
                disabled={!formData.title}
                title="Generate Code"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="16 18 22 12 16 6"></polyline>
                  <polyline points="8 6 2 12 8 18"></polyline>
                </svg>
                Generate
              </button>
            </div>
            {errors.code && <span className="error-message">{errors.code}</span>}
          </div>

          <div className="section-divider">
            <h3>Schedule & Targeting</h3>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startDate">Start Date *</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                min={getMinDate()}
                className={errors.startDate ? 'error' : ''}
              />
              {errors.startDate && <span className="error-message">{errors.startDate}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="endDate">End Date *</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                min={formData.startDate || getMinDate()}
                className={errors.endDate ? 'error' : ''}
              />
              {errors.endDate && <span className="error-message">{errors.endDate}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="targetAudience">Target Audience</label>
            <select
              id="targetAudience"
              name="targetAudience"
              value={formData.targetAudience}
              onChange={handleChange}
              className="form-select"
            >
              {targetAudiences.map(audience => (
                <option key={audience} value={audience}>{audience}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="image">Promotion Icon</label>
            <div className="emoji-selector">
              <input
                type="text"
                id="image"
                name="image"
                value={formData.image}
                onChange={handleChange}
                maxLength="2"
                className="emoji-input"
                placeholder="🎯"
              />
              <div className="emoji-preview">{formData.image}</div>
            </div>
            <div className="emoji-suggestions">
              {emojis.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  className={`emoji-option ${formData.image === emoji ? 'selected' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, image: emoji }))}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save">
              {isEditMode ? 'Update Promotion' : 'Create Promotion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromotionModal;



